import React, { useContext, useState, useLayoutEffect, useEffect } from "react";
import debug from "../../utils/debug";
import { get } from "../../utils/helpers";
import { VerifySalesPersonIdResponse } from "../../utils/api";
import {
  APP_PAGE,
  checkAppCorrectlySetup,
  checkHasVerifiedId,
  actions
} from "../../hooks/useAppState";
import appEvents from "../../utils/appEvents";
import api from "../../utils/api";
import { addCustomerNotFoundNotification } from "../../utils/notifications";
import { setCustomerDataState } from "../../hooks/useCustomerDataState";
import Modal from "../../components/Modal";
import PageLoading from "../../components/PageLoading";
import VerifySalesPersonForm from "../../components/VerifySalesPersonForm";
import { AppStateContext } from "../../containers/App";
import SetupPage from "../../containers/SetupPage";
import ActionsPage from "../../containers/ActionsPage";
import SearchPage from "../../containers/SearchPage";
import HistoryPage from "../../containers/HistoryPage";
import NotificationsPage from "../../containers/NotificationsPage";
import CustomerPage from "../../containers/CustomerPage";
import NotFoundPage from "../../containers/NotFoundPage";

function getPageComponentToRender(page: string | number): Function {
  let RenderPage = PageLoading;
  switch (page) {
    case APP_PAGE.SETUP:
      RenderPage = SetupPage;
      break;

    case APP_PAGE.ACTIONS:
      RenderPage = ActionsPage;
      break;

    case APP_PAGE.CUSTOMER:
      RenderPage = CustomerPage;
      break;

    case APP_PAGE.SEARCH:
      RenderPage = SearchPage;
      break;

    case APP_PAGE.HISTORY:
      RenderPage = HistoryPage;
      break;

    case APP_PAGE.NOTIFICATIONS:
      RenderPage = NotificationsPage;
      break;

    default:
      RenderPage = NotFoundPage;
      break;
  }
  return RenderPage;
}

export default function AppRouter() {
  // Shared state
  const [appState, doAppStateAction] = useContext(AppStateContext);
  const config = appState.config;
  const isAppSetup = checkAppCorrectlySetup(appState);
  const hasVerifiedId = config.customise.verifySalesPerson.enable
    ? checkHasVerifiedId(appState)
    : true;

  // Internal state
  const [currentPage, setCurrentPage] = useState(appState.page);
  const [verificationRequired, setVerificationRequired] = useState(false);

  let RenderPage = getPageComponentToRender(currentPage);

  // get customer id from param
  const urlParams = new URLSearchParams(window.location.search);
  const customerId: string = encodeURIComponent(urlParams.get("id")! || "");
  if (customerId) {
    RenderPage = PageLoading;
    api
      .getCustomerById(customerId)
      .then(customerData => {
        if (customerData && customerData.identifier) {
          setCustomerDataState(customerData);
          doAppStateAction(actions.setPage(APP_PAGE.CUSTOMER));
        } else {
          console.log("not found");
        }
      })
      .catch(err => {
        console.log("not found");
        addCustomerNotFoundNotification(customerId);
      });
    // clear id param
    const params = new URLSearchParams(window.location.search);
    params.delete("id");
    const newPath = window.location.pathname + "?" + params.toString();
    window.history.pushState({}, document.title, newPath);
  }

  // Event handlers
  const handleVerifySuccess = ({
    salesPersonId
  }: VerifySalesPersonIdResponse): void => {
    debug("[AppRouter] handleVerifySuccess", { appState, salesPersonId });
    setVerificationRequired(false);
    doAppStateAction(actions.setSalesPerson(salesPersonId));
  };

  const handleClickCancel = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    debug("[AppRouter] cancel verify sales person");
    setVerificationRequired(false);
    doAppStateAction(actions.setPage(currentPage));
  };

  appEvents.on("app.resetStore", () => {
    if (appState.storeId) {
      doAppStateAction(actions.setStoreId(""));
      setCurrentPage(APP_PAGE.SETUP);
    }
  });

  useLayoutEffect(() => {
    debug("[AppRouter] useLayoutEffect", {
      currentPage,
      appPage: appState.page
    });

    // App not setup yet, navigate to setup page
    if (!isAppSetup) {
      debug(
        `[AppRouter] App not setup yet: transitioning from ${currentPage} to ${appState.page}`,
        { appState }
      );
      setCurrentPage(APP_PAGE.SETUP);
    }

    // Change to next page via appState.page differing from the router's current page
    if (currentPage !== appState.page) {
      debug(
        `[AppRouter] Transitioning from ${currentPage} to ${appState.page}`,
        { appState }
      );

      // Logic to figure out what page to display
      switch (appState.page) {
        // Does not require ID verification
        case APP_PAGE.ACTIONS:
          debug(`[AppRouter] show ${appState.page}`);
          setCurrentPage(appState.page);

          if (
            get(config, "customise.verifySalesPerson.resetOnActions") === true
          ) {
            // reset salesPersonId
            doAppStateAction(actions.setSalesPerson(""));
          }
          break;

        // Only requires ID verification if already setup
        case APP_PAGE.NOTIFICATIONS:
        case APP_PAGE.SETUP:
          if (!isAppSetup || hasVerifiedId) {
            debug(`[AppRouter] show ${appState.page}`);
            setCurrentPage(appState.page);
          } else {
            debug(
              `[AppRouter] show verify sales person form before displaying ${appState.page}`
            );
            doAppStateAction(actions.setSalesPerson(""));
            setVerificationRequired(true);
          }
          break;

        // Requires sales person ID verification
        case APP_PAGE.SEARCH:
        case APP_PAGE.HISTORY:
        case APP_PAGE.CUSTOMER:
          if (hasVerifiedId) {
            debug(`[AppRouter] show ${appState.page}`);
            setCurrentPage(appState.page);
          } else {
            debug(
              `[AppRouter] show verify sales person form before displaying ${appState.page}`
            );
            doAppStateAction(actions.setSalesPerson(""));
            setVerificationRequired(true);
          }
          break;

        default:
      }
    }
  }, [appState.page, appState.salesPersonId]);

  // When navigated to the intended page, ensure the window is scrolled to the top
  useEffect(() => {
    const appMainNode = document.querySelector(".app-main");
    if (
      appState.page === currentPage &&
      appMainNode &&
      appMainNode.scrollIntoView
    ) {
      appMainNode.scrollIntoView({
        block: "start"
      });
    }
  }, [appState.page, currentPage]);

  return (
    <>
      <RenderPage />
      <Modal className="verify-salesperson" isOpen={verificationRequired}>
        <VerifySalesPersonForm
          onVerifySuccess={handleVerifySuccess}
          onCancel={handleClickCancel}
        />
      </Modal>
    </>
  );
}
